import type { TPostAttachment } from '@onesource/schemas';
import { axiosServiceInstance } from '@onesource/services';

import { compressPdfFile } from '../media/compress-pdf';

export type TPostAttachmentType = TPostAttachment['type'];

/**
 * A single attachment in the composer / edit form. Covers both freshly picked
 * files (mid/just-uploaded, `file` set) and already-persisted attachments
 * seeded when editing a post (`file` undefined, `url` set).
 */
export interface PostAttachmentItem {
  id: string;
  // Object URL for a freshly picked file, or the remote URL for an existing one.
  preview: string;
  // Resolved remote URL — present once uploaded, or immediately for existing.
  url?: string;
  uploading: boolean;
  type: TPostAttachmentType;
  name?: string;
  mimeType?: string;
  size?: number;
  // Backing media_files row, present for compressible uploads (video / PDF).
  mediaId?: string;
}

// Posts accept only images, PDFs, and videos, capped per file.
export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_SIZE_MB = 200;
// Formats every member can attach: images and PDFs.
const ATTACHMENT_ACCEPT_BASE = 'image/*,application/pdf';
// Full set including video — the default, for users allowed to upload video.
export const ATTACHMENT_ACCEPT = `${ATTACHMENT_ACCEPT_BASE},video/*`;
// Same set without video — used when the current user can't upload videos.
export const ATTACHMENT_ACCEPT_NO_VIDEO = ATTACHMENT_ACCEPT_BASE;

export const detectAttachmentType = (file: File): TPostAttachmentType => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  if (file.type.startsWith('video/')) {
    return 'video';
  }
  return 'file';
};

/**
 * Whether a picked file is an accepted attachment: images and PDFs for everyone,
 * plus videos only when `allowVideo` (admins/space managers). Guards against
 * drag/paste bypassing the file input's `accept` filter.
 */
export const isAcceptedAttachment = (file: File, allowVideo: boolean): boolean => {
  if (file.type.startsWith('image/') || file.type === 'application/pdf') {
    return true;
  }
  return allowVideo && file.type.startsWith('video/');
};

const getPresignedUrl = async (
  key: string,
  contentType: string,
): Promise<{ uploadUrl: string; publicUrl: string | null }> => {
  const res = await axiosServiceInstance.get<{ uploadUrl: string; publicUrl: string | null }>('/upload/presigned-url', {
    params: { key, contentType, folder: 'community-hub-posts' },
  });
  return res.data;
};

/**
 * Strip the origin from an upload URL, keeping the path exactly as the object
 * key was written.
 *
 * Deliberately not `new URL(url).pathname`: that percent-encodes the path, and
 * the stored value is later used as the R2 object key by the transcoder and the
 * retention sweep. Re-encoding it at request time would double-encode
 * (`my video.mp4` → `my%2520video.mp4`) and miss the object. `pathname` also
 * truncates at a `#` in the filename, silently losing the extension.
 */
const toStoredPath = (url: string): string => {
  if (!url.startsWith('http')) {
    return url;
  }
  // slice() rather than pathname — preserves the raw key, including `#` and spaces.
  return url.slice(new URL(url).origin.length);
};

/**
 * Register the upload in `media_files` so async compression has a row to track.
 * Only videos still compress server-side (images are resized and PDFs are
 * compressed in the browser before upload), so everything else skips this
 * round-trip and attaches directly to its uploaded URL.
 *
 * Best-effort: a failure here costs compression, not the post, so the caller
 * still gets a usable attachment.
 */
const createMediaRecord = async (file: File, publicUrl: string): Promise<string | undefined> => {
  const isCompressible = file.type.startsWith('video/');
  if (!isCompressible) {
    return undefined;
  }

  try {
    const res = await axiosServiceInstance.post<{ id: string }>('/media', {
      originalName: file.name,
      // Stored as a path, matching what the media-manager writes.
      cdnUrls: { original: toStoredPath(publicUrl) },
      mimeType: file.type,
      sizeBytes: file.size,
    });
    return res.data.id;
  } catch {
    return undefined;
  }
};

/**
 * Upload a file via the presigned-PUT R2 flow.
 *
 * Returns the public URL plus, for compressible types, the `media_files` id.
 * Readers use that id to serve the compressed derivative once the transcoder
 * has produced one.
 */
export const uploadPostFile = async (input: File): Promise<{ url: string; mediaId?: string }> => {
  // Compress PDFs in the browser before upload — they no longer transcode
  // server-side, so the uploaded object is already the final, smaller file.
  const file = input.type === 'application/pdf' ? await compressPdfFile(input) : input;
  const key = `${Date.now()}-${file.name}`;
  const { uploadUrl, publicUrl } = await getPresignedUrl(key, file.type);

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  // Previously unchecked — a failed PUT silently produced an attachment
  // pointing at an object that was never written.
  if (!response.ok) {
    throw new Error(`Upload failed for ${file.name} (${response.status})`);
  }

  const url = publicUrl || uploadUrl.split('?')[0];
  // Videos get a media_files row the transcoder drives; other types skip it
  // inside createMediaRecord and attach directly to their uploaded URL.
  return { url, mediaId: await createMediaRecord(file, url) };
};

/** Map a resolved item to the persisted attachment shape (null if not uploaded). */
export const itemToAttachment = (item: PostAttachmentItem): TPostAttachment | null => {
  if (!item.url) {
    return null;
  }
  return {
    url: item.url,
    type: item.type,
    name: item.name,
    mimeType: item.mimeType,
    size: item.size,
    mediaId: item.mediaId,
  };
};

/** Seed an item from an already-persisted attachment (for the edit form). */
export const attachmentToItem = (attachment: TPostAttachment): PostAttachmentItem => ({
  id: attachment.url,
  preview: attachment.url,
  url: attachment.url,
  uploading: false,
  type: attachment.type,
  name: attachment.name,
  mimeType: attachment.mimeType,
  size: attachment.size,
  mediaId: attachment.mediaId,
});
